import apiClient from './apiClient';

export interface Appointment {
  id: string;
  patientId: string;
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  providerId: string;
  provider?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  date: string;
  time: string;
  reason: string;
  duration: number;
  status: 'scheduled' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled' | 'no-show';
  notes?: string;
  cancellationReason?: string;
  appointmentType: 'routine' | 'follow-up' | 'urgent' | 'emergency' | string;
  isVirtual: boolean;
  virtualMeetingLink?: string;
  bookingMode?: 'time' | 'queue';
  queuePosition?: number | null;
  arrivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WalkInQueueEntry {
  appointmentId: string;
  patientId: string;
  patientName: string;
  queuePosition: number | null;
  arrivedAt: string | null;
  bookedAt: string;
  status: string;
}

export interface WalkInAwaitingEntry {
  appointmentId: string;
  patientId: string;
  patientName: string;
  reservationNumber: number | null;
  bookedAt: string;
  status: string;
}

export interface WalkInQueueProvider {
  providerId: string | null;
  providerName: string;
  bookedCount: number;
  arrivedCount: number;
  remainingCapacity: number | null;
  queue: WalkInQueueEntry[];
  awaitingArrival: WalkInAwaitingEntry[];
}

export interface WalkInQueueResponse {
  date: string;
  providers: WalkInQueueProvider[];
}

export interface FilterAppointmentDto {
  page?: number;
  limit?: number;
  orderBy?: string;
  order?: 'ASC' | 'DESC';
  patientId?: string;
  providerId?: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  status?: 'scheduled' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled' | 'no-show';
  appointmentType?: 'routine' | 'follow-up' | 'urgent' | 'emergency';
  isVirtual?: boolean;
  reason?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  message?: string;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  timestamp: string;
  path?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  timestamp: string;
  path?: string;
}

export interface CreateAppointmentDto {
  patientId: string;
  providerId?: string;
  date: string;
  time: string;
  reason: string;
  duration?: number;
  status?: 'scheduled' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled' | 'no-show';
  notes?: string;
  appointmentType?: 'routine' | 'follow-up' | 'urgent' | 'emergency';
  isVirtual?: boolean;
  virtualMeetingLink?: string;
}

export interface UpdateAppointmentDto extends Partial<CreateAppointmentDto> {
  cancellationReason?: string;
}

export interface AppointmentStatistics {
  total: number;
  todaysAppointments: number;
  byStatus: Array<{ status: string; count: number }>;
  byType: Array<{ type: string; count: number }>;
}

/**
 * Appointment API Service
 * Handles all appointment-related API calls
 */
export const appointmentApi = {
  /**
   * Get paginated list of appointments with optional filters
   */
  getAppointments: async (filters?: FilterAppointmentDto): Promise<PaginatedResponse<Appointment>> => {
    const params = new URLSearchParams();

    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.orderBy) params.append('orderBy', filters.orderBy);
    if (filters?.order) params.append('order', filters.order);
    if (filters?.patientId) params.append('patientId', filters.patientId);
    if (filters?.providerId) params.append('providerId', filters.providerId);
    if (filters?.date) params.append('date', filters.date);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.appointmentType) params.append('appointmentType', filters.appointmentType);
    if (filters?.isVirtual !== undefined) params.append('isVirtual', filters.isVirtual.toString());
    if (filters?.reason) params.append('reason', filters.reason);

    const response = await apiClient.get<PaginatedResponse<Appointment>>(
      `/appointments${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a single appointment by ID
   */
  getAppointmentById: async (id: string): Promise<ApiResponse<Appointment>> => {
    const response = await apiClient.get<ApiResponse<Appointment>>(`/appointments/${id}`);
    return response.data;
  },

  /**
   * How many days ahead this clinic allows booking (default 30, 0 =
   * unlimited). Used to bound the booking date picker to the same window
   * the backend enforces.
   */
  getBookingHorizon: async (): Promise<ApiResponse<{ days: number }>> => {
    const response = await apiClient.get<ApiResponse<{ days: number }>>(
      '/appointments/booking-horizon',
    );
    return response.data;
  },

  /**
   * Create a new appointment
   */
  createAppointment: async (appointmentData: CreateAppointmentDto): Promise<ApiResponse<Appointment>> => {
    const response = await apiClient.post<ApiResponse<Appointment>>('/appointments', appointmentData);
    return response.data;
  },

  /**
   * Update an existing appointment
   */
  updateAppointment: async (id: string, appointmentData: UpdateAppointmentDto): Promise<ApiResponse<Appointment>> => {
    const response = await apiClient.patch<ApiResponse<Appointment>>(`/appointments/${id}`, appointmentData);
    return response.data;
  },

  /**
   * Delete an appointment (soft delete)
   */
  deleteAppointment: async (id: string): Promise<void> => {
    await apiClient.delete(`/appointments/${id}`);
  },

  /**
   * Get appointment statistics
   */
  getAppointmentStatistics: async (): Promise<ApiResponse<AppointmentStatistics>> => {
    const response = await apiClient.get<ApiResponse<AppointmentStatistics>>('/appointments/statistics');
    return response.data;
  },

  /**
   * Mark a walk-in patient as physically present. Returns 409 if
   * already checked in, 400 for non-queue or finalised rows.
   */
  checkInAppointment: async (id: string): Promise<ApiResponse<Appointment>> => {
    const response = await apiClient.post<ApiResponse<Appointment>>(
      `/appointments/${id}/check-in`,
    );
    return response.data;
  },

  /**
   * Today's walk-in queue across all providers for the active
   * tenant. Polled by the receptionist + doctor home cards.
   */
  getTodayWalkInQueue: async (): Promise<ApiResponse<WalkInQueueResponse>> => {
    const response = await apiClient.get<ApiResponse<WalkInQueueResponse>>(
      '/appointments/queue/today',
    );
    return response.data;
  },
};

export default appointmentApi;
